import { Visibility as PreviewIcon } from "@mui/icons-material";
import { Modal } from "@mui/material";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { flushSync } from "react-dom";

import { MaterialRequest, MaterialRequestExport } from "../../../../data/entities";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import DocxPreviewModal from "../../../../shared/components/DocxPreviewModal";
import LoadingButton from "../../../../shared/components/LoadingButton";
import { api } from "../../../../shared/hooks/api";
import useMergeState from "../../../../shared/hooks/useMergeState";
import Template from "../../../../templates/export_material_template.docx";
import { generateDocumentBlob } from "../../../../utils/exportUtils";
import { formatNumber } from "../../../../utils/format";
import { error, success, warning } from "../../../../utils/toast";
import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../../MaterialOrder/Styled";
import { getStorageForRole } from "../../../Products/Title/ExportProductModal";
import { ExportMaterialForm } from "./ExportMaterialForm";
import { MaterialExportDetails } from "./MaterialExportDetails";
import { ExpiredStockUsage, getMaterialRequestStock } from "./stock";

interface ExportMaterialModalProps {
    open: boolean;
    handleClose: () => void;
    materialRequest: MaterialRequest | undefined;
    materialExports: MaterialRequestExport[];
    handleConfirm?: () => void;
}

const getInitialState = (materialRequest: MaterialRequest | undefined, role: ROLE) => {
    const selectedMaterials = materialRequest?.materials?.map((item: any) => {
        const material = item;
        const requestQuantity = item.request_quantity || 0;

        return {
            ...material,
            request_quantity: requestQuantity,
            exportQuantity: requestQuantity - (item.exported_quantity || 0),
            available_quantity: item.available_quantity ?? item.quantity,
            note: "",
        };
    }) || [];

    const currentDate = new Date().toISOString().split("T")[0];

    return {
        formData: {
            storage: getStorageForRole(role),
            export_method: "Xuất sản xuất",
            department: "",
            receiverer_name: "",
            export_date: currentDate,
            note: "",
        },
        selectedMaterials,
    };
};

export const ExportMaterialModal = ({
                                        open,
                                        handleClose,
                                        materialRequest,
                                        materialExports,
                                        handleConfirm,
                                    }: ExportMaterialModalProps) => {
    const { user: { role } } = useContext(AuthenticationContext);

    const [state, mergeState] = useMergeState(getInitialState(materialRequest, role));
    const [{ isCreating }, createExport] = api.post("/materials/exports/");
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

    if (!materialRequest) {
        return null;
    }

    const processMaterialExports = (materials: any, materialExports: MaterialRequestExport[]) => {
        const materialExportsData = materialExports.map((item) => {
            return {
                id: item.material.id,
                name: item.material.name,
                request_quantity: item.request_quantity,
                exported_quantity: item.quantity,
                material_code: item.material.material_code,
                need_to_export_quantity: item.request_quantity - item.quantity,
            };
        });

        const requestQuantityDictionary = materials.reduce((acc: any, item: any) => {
            acc[item.id] = item.request_quantity;
            return acc;
        }, {});

        const sumExportedQuantityDictionary = materialExportsData.reduce((acc: any, item: any) => {
            if (acc[item.id]) {
                acc[item.id] += item.exported_quantity;
            } else {
                acc[item.id] = item.exported_quantity;
            }
            return acc;
        }, {});

        const processedMaterials = Object.keys(requestQuantityDictionary).map((id) => {
            const requestQty = requestQuantityDictionary[id];
            const exportedQty = sumExportedQuantityDictionary[id] || 0;

            if (exportedQty < requestQty) {
                return {
                    id,
                    name: materials.find((m: any) => m.id === id)?.name,
                    request_quantity: requestQty,
                    exported_quantity: exportedQty,
                    material_code: materials.find((m: any) => m.id === id)?.material_code,
                    need_to_export_quantity: requestQty - exportedQty,
                };
            }
            return null;
        }).filter(Boolean);

        return processedMaterials;
    };

    const mergeFormData = (newFormData: any) => {
        mergeState({ formData: { ...state.formData, ...newFormData } });
    };

    const mergeSelectedMaterials = (newSelectedMaterials: any) => {
        mergeState({ selectedMaterials: newSelectedMaterials });
    };

    const handleCloseWithAnimation = () => {
        setTimeout(handleClose, 200);
    };

    const validateExportData = () => {
        const errors: string[] = [];

        if (!state.formData.storage) {
            errors.push("Kho xuất không được để trống.");
        }

        if (!state.formData.export_method) {
            errors.push("Phương thức xuất không được để trống.");
        }

        if (!state.formData.department) {
            errors.push("Khách hàng không được để trống.");
        }

        if (!state.formData.export_date || isNaN(new Date(state.formData.export_date).getTime())) {
            errors.push("Ngày xuất kho không hợp lệ.");
        }

        if (!state.selectedMaterials.length) {
            errors.push("Danh sách vật tư không được để trống.");
        }

        state.selectedMaterials.forEach((material: any, index: number) => {
            const requestQuantity = material.request_quantity || 0
            if (!material.exportQuantity || material.exportQuantity <= 0) {
                errors.push(`Vật tư ${index + 1}: Số lượng xuất phải lớn hơn 0.`);
            }
            const { total } = getMaterialRequestStock(material);
            if (material.exportQuantity > total) {
                errors.push(`Vật tư ${index + 1}: Số lượng xuất không được vượt quá tổng số lượng được phép xuất (${formatNumber(total)}).`);
            }
            if (material.exportQuantity > (requestQuantity - material.exported_quantity)) {
                errors.push(`Vật tư ${index + 1}: Số lượng xuất không được vượt quá số lượng yêu cầu (${formatNumber(requestQuantity - material.exported_quantity)}).`);
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    const handlePreview = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert("Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" + validation.errors.join("\n\n"));
            return;
        }

        const itemDate = new Date(state.formData.export_date);
        const exportData = {
            storage: state.formData.storage || "",
            receiverer_name: state.formData.receiverer_name || "",
            export_method: state.formData.export_method || "",
            department: state.formData.department || "",
            source: state.formData.source || "",
            note: state.formData.note || "",
            day: itemDate.getDate(),
            month: itemDate.getMonth() + 1,
            year: itemDate.getFullYear(),
            export_code: "PHIẾU XEM TRƯỚC",
            materials: state.selectedMaterials.map((material: any, index: number) => ({
                index: index + 1,
                name: material.name || "",
                quantity: material.exportQuantity || "",
                measurement_unit: material.measurement_unit || "",
                code: material.material_code || material.id || "",
            })),
        };

        try {
            const blob = await generateDocumentBlob(exportData, Template);
            console.debug("Generated DOCX blob for preview", { size: blob.size, type: blob.type });
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const handleExport = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert(validation.errors.join("\n\n"));
            return;
        }

        try {
            const exportData = {
                ...state.formData,
                materials: state.selectedMaterials.map((item: any) => ({
                    material: item.id,
                    quantity: item.exportQuantity,
                    note: item.note || "",
                })),
                content_type: "MaterialRequest",
                object_id: materialRequest.id,
            };

            const response = await createExport(exportData) as { expired_stock_usage?: ExpiredStockUsage[] };
            const expiredUsage = (response?.expired_stock_usage ?? []).filter(item => item.quantity > 0);
            setIsPreviewOpen(false);
            setPreviewBlob(null);
            setTimeout(() => {
                flushSync(() => {
                    handleClose();
                    handleConfirm?.();
                });
                // Let React remove both modal/backdrop trees before notifying.
                setTimeout(() => {
                    success("THÀNH CÔNG", "Xuất kho vật tư thành công");
                    if (expiredUsage.length) {
                        warning("Đã sử dụng vật tư hết hạn", (
                            <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
                                {expiredUsage.map(item => (
                                    <li key={item.material}>
                                        {`${item.material_code}: ${formatNumber(item.quantity)} ${item.measurement_unit}`}
                                        {Boolean(item.deductions?.length) && (
                                            <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                                                {item.deductions!.map((deduction, index) => (
                                                    <li key={index}>
                                                        {`Lô hết hạn ${dayjs(deduction.due_date).format("DD/MM/YYYY")} : ${formatNumber(deduction.quantity)} ${item.measurement_unit}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        ));
                    }
                }, 0);
            }, 200);
        } catch (err) {
            error("Không thể xuất kho vật tư", err);
        }
    };

    const onChangeExportQuantity = (id: string, quantity: number) => {
        const selectedMaterial = state.selectedMaterials.find(
            (material: any) => material.id === id,
        );
        const { total } = getMaterialRequestStock(selectedMaterial ?? {});
        if (quantity > total) {
            warning(
                "Số lượng xuất vượt quá tổng số lượng được phép xuất",
                `Có thể xuất: ${formatNumber(total)}`,
            );
            return;
        }
        const updatedMaterials = state.selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, exportQuantity: quantity } : material,
        );
        mergeSelectedMaterials(updatedMaterials);
    };

    const onChangeExportNote = (id: string, note: string) => {
        const updatedMaterials = state.selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, note } : material,
        );
        mergeSelectedMaterials(updatedMaterials);
    };

    const onChangeExportDate = (id: string, date: string | null) => {
        const updatedMaterials = state.selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, export_date: date } : material,
        );
        mergeSelectedMaterials(updatedMaterials);
    };

    const removeSelectedItem = (item: any) => {
        const updatedMaterials = state.selectedMaterials.filter((material: any) => material.id !== item.id);
        mergeSelectedMaterials(updatedMaterials);
    };


    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseWithAnimation}
                closeAfterTransition
                BackdropComponent={StyledBackdrop}
                BackdropProps={{
                    timeout: 500,
                    onClick: (e) => e.stopPropagation(),
                }}
            >
                <ModalWrapper isOpen={open}>
                    <Expandable>
                        <TitleWrapper>
                            <TitleText>Xuất kho vật tư từ yêu cầu</TitleText>
                            <InfoText>Vui lòng điền thông tin xuất kho và cấu hình số lượng cho từng vật tư</InfoText>
                        </TitleWrapper>

                        <ExportMaterialForm formData={state.formData} onChange={mergeFormData} />

                        <MaterialExportDetails
                            materials={state.selectedMaterials}
                            onChangeExportQuantity={onChangeExportQuantity}
                            onChangeExportNote={onChangeExportNote}
                            onChangeMaterialRequestedQuantity={onChangeExportQuantity}
                            removeSelectedItem={removeSelectedItem}
                        />


                    </Expandable>

                    <ButtonRow>
                        <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                        <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">
                            Xem trước phiếu
                        </StyledButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
            
            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu xuất kho"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={handleExport}
                        disabled={isCreating}
                        isLoading={isCreating}
                        text="Xuất kho"
                        style={undefined}
                    />
                }
            />
        </>
    );
};
